import { useEffect, useState } from 'react';
import './styles.css';
import { useNavigate } from 'react-router-dom';
import { LayoutHeader as Header } from '@/layouts';
import { LayoutFooter as Footer } from '@/layouts';
import { LayoutVehicleCard as VehicleCard } from '@/layouts';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { openWhatsApp } from '@/lib/whatsapp';
import { generateVehicleSlug } from '@/lib/slug';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Heart, Trash2, MessageCircle, GitCompare, Check } from 'lucide-react';
import { useAppData } from '@/contexts/AppDataContext';
import { useTheme } from '@/contexts/ThemeContext';
import { layout4ThemeStyle } from './theme';
import { useStore } from '@/contexts/StoreContext';
import { secureGetItem, secureRemoveItem } from '@/lib/storage';

const Favorites = () => {
  const { config, vehicles } = useAppData();
  const { selectedStore } = useStore();
  const store = selectedStore || config.stores[0];
  const navigate = useNavigate();
  const [favoriteVehicles, setFavoriteVehicles] = useState<any[]>([]);
  const [compareMode, setCompareMode] = useState(false);
  const [selectedForCompare, setSelectedForCompare] = useState<number[]>([]);
  const [compareModalOpen, setCompareModalOpen] = useState(false);

  const { colors } = useTheme();
  const themeStyle = layout4ThemeStyle(colors);
  const buttonColor = colors.button;
  const primaryColor = colors.primary;

  const handleClearFavorites = async () => {
    await secureRemoveItem('favorites');
    setFavoriteVehicles([]);
    window.dispatchEvent(new Event('favoritesUpdated'));
  };

  const handleToggleCompareMode = () => {
    setCompareMode(!compareMode);
    if (compareMode) {
      setSelectedForCompare([]);
    }
  };

  const handleToggleVehicleForCompare = (vehicleId: number) => {
    setSelectedForCompare(prev => {
      if (prev.includes(vehicleId)) {
        return prev.filter(id => id !== vehicleId);
      } else if (prev.length < 3) {
        return [...prev, vehicleId];
      }
      return prev;
    });
  };

  const handleOpenCompare = () => {
    if (selectedForCompare.length >= 2) {
      setCompareModalOpen(true);
    }
  };

  const compareVehicles = favoriteVehicles.filter(v => selectedForCompare.includes(v.id));

  useEffect(() => {
    const loadFavorites = async () => {
      const favoriteIds = await secureGetItem('favorites');
      if (favoriteIds && Array.isArray(favoriteIds)) {
        const favorites = vehicles.filter(v => favoriteIds.includes(v.id));
        setFavoriteVehicles(favorites);
      } else {
        setFavoriteVehicles([]);
      }
    };

    loadFavorites();

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'favorites') {
        loadFavorites();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    const handleFavoritesUpdate = () => {
      loadFavorites();
    };

    window.addEventListener('favoritesUpdated', handleFavoritesUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('favoritesUpdated', handleFavoritesUpdate);
    };
  }, [vehicles]);

  useEffect(() => {
    document.title = `Meus Favoritos - ${config.name}`;
  }, [config.name]);

  return (
    <div className="layout-4 min-h-screen bg-[var(--wl4-bg)]" style={themeStyle}>
      <Header config={config} />

      <div className="wl4-page-banner">
        <div className="wl4-container">
          <p className="wl4-eyebrow mb-2">Favoritos</p>
          <h1>Meus Favoritos</h1>
          <p className="mt-2">
            {favoriteVehicles.length}{' '}
            {favoriteVehicles.length === 1 ? 'veículo salvo' : 'veículos salvos'}
          </p>
        </div>
      </div>

      <section className="wl4-section bg-white !pt-8 !pb-16">
        <div className="wl4-container">
          {favoriteVehicles.length > 0 ? (
            <>
              <div className="wl4-section-header !mb-8">
                <div>
                  <p className="wl4-eyebrow">Sua lista</p>
                  <h2 className="wl4-h2 mt-1">Veículos salvos</h2>
                  <p className="wl4-lead mt-1 text-sm">
                    {compareMode
                      ? 'Selecione de 2 a 3 veículos para comparar'
                      : 'Acesse rapidamente os veículos que você curtiu'}
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-2 shrink-0">
                  {favoriteVehicles.length >= 2 && (
                    <button
                      type="button"
                      className={`wl4-favorites-action ${compareMode ? 'is-active' : ''}`}
                      onClick={handleToggleCompareMode}
                    >
                      <GitCompare className="h-4 w-4" />
                      {compareMode ? 'Cancelar' : 'Comparar'}
                    </button>
                  )}

                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button type="button" className="wl4-favorites-action is-danger">
                        <Trash2 className="h-4 w-4" />
                        Limpar todos
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="layout-4 wl4-favorites-alert">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Limpar todos os favoritos?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Esta ação removerá todos os {favoriteVehicles.length} veículos da sua lista de favoritos.
                          Esta ação não pode ser desfeita.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleClearFavorites}
                          className="bg-[var(--wl4-accent)] hover:opacity-90"
                        >
                          Sim, limpar tudo
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {favoriteVehicles.map((vehicle) => (
                  <div key={vehicle.id} className="relative">
                    {compareMode && (
                      <button
                        type="button"
                        className={`wl4-favorites-compare-check ${selectedForCompare.includes(vehicle.id) ? 'is-selected' : ''}`}
                        onClick={() => handleToggleVehicleForCompare(vehicle.id)}
                        aria-label={selectedForCompare.includes(vehicle.id) ? 'Desmarcar comparação' : 'Selecionar para comparar'}
                      >
                        {selectedForCompare.includes(vehicle.id) && (
                          <Check className="h-4 w-4" />
                        )}
                      </button>
                    )}
                    <VehicleCard
                      vehicle={vehicle}
                      whatsappUrl={store.whatsapp?.ios}
                    />
                  </div>
                ))}
              </div>

              <div className="wl4-services-help mt-12 pt-10 border-t border-[var(--wl4-border)] flex flex-col md:flex-row md:items-center justify-between gap-6 text-center md:text-left">
                <div>
                  <h2 className="wl4-h2">Gostou de algum veículo?</h2>
                  <p className="text-[var(--wl4-muted)] mt-2 max-w-md mx-auto md:mx-0">
                    Entre em contato para mais informações, agendar test-drive ou simular financiamento.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 justify-center shrink-0">
                  <Button
                    size="lg"
                    className="rounded-lg text-white font-semibold h-12 px-6"
                    style={{ backgroundColor: buttonColor }}
                    onClick={() => openWhatsApp(store.whatsapp?.ios)}
                  >
                    <MessageCircle className="h-5 w-5 mr-2" />
                    WhatsApp
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="rounded-lg border-[var(--wl4-border)] text-[var(--wl4-text)] h-12 px-6 hover:bg-[var(--wl4-bg)]"
                    onClick={() => navigate('/estoque')}
                  >
                    Ver estoque
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="wl4-favorites-empty">
              <div className="wl4-favorites-empty-icon">
                <Heart className="h-8 w-8" />
              </div>
              <h2 className="wl4-h2 mt-6">Nenhum favorito ainda</h2>
              <p className="wl4-lead mt-2 max-w-md mx-auto text-sm">
                Você ainda não adicionou nenhum veículo aos favoritos.
                Explore nosso estoque e salve seus preferidos!
              </p>
              <Button
                size="lg"
                className="mt-8 rounded-lg text-white font-semibold h-12 px-8"
                style={{ backgroundColor: primaryColor }}
                onClick={() => navigate('/estoque')}
              >
                Ver estoque
              </Button>
            </div>
          )}
        </div>
      </section>

      <Footer config={config} />

      {compareMode && selectedForCompare.length >= 2 && (
        <div className="wl4-favorites-compare-bar">
          <button type="button" className="wl4-favorites-compare-bar-btn" onClick={handleOpenCompare}>
            <GitCompare className="h-5 w-5" />
            Comparar {selectedForCompare.length} veículos
          </button>
        </div>
      )}

      <Dialog open={compareModalOpen} onOpenChange={setCompareModalOpen}>
        <DialogContent
          className="layout-4 wl4-favorites-compare-modal max-w-6xl max-h-[90vh] p-0 flex flex-col gap-0 [&>button]:hidden overflow-hidden"
          style={themeStyle}
        >
          <DialogHeader className="wl4-favorites-compare-head">
            <DialogTitle className="flex items-center gap-2 text-[var(--wl4-text)]">
              <GitCompare className="h-5 w-5 text-[var(--wl4-accent)]" />
              Comparar veículos
            </DialogTitle>
          </DialogHeader>

          <div className="overflow-y-auto flex-1">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="sticky top-0 bg-white z-10 shadow-sm">
                  <tr className="border-b border-[var(--wl4-border)]">
                    <th className="text-left p-4 font-semibold text-sm bg-white" />
                    {compareVehicles.map(vehicle => (
                      <th key={vehicle.id} className="p-4 max-w-[100px] bg-white">
                        <div className="space-y-2">
                          <img
                            src={vehicle.photos?.[0] || '/placeholder.svg'}
                            alt={`${vehicle.brand} ${vehicle.model}`}
                            className="w-full h-32 object-cover rounded-lg"
                          />
                          <p className="font-semibold text-sm text-[var(--wl4-text)]">
                            {vehicle.brand} {vehicle.model}
                          </p>
                          <p className="text-xs text-[var(--wl4-muted)]">
                            {vehicle.version}
                          </p>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--wl4-border)]">
                  <tr className="hover:bg-[var(--wl4-bg)]">
                    <td className="p-4 font-medium text-sm text-[var(--wl4-text)]">Preço</td>
                    {compareVehicles.map(vehicle => (
                      <td key={vehicle.id} className="p-4 text-center">
                        <p className="font-semibold text-[var(--wl4-accent)]">
                          {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(vehicle.price)}
                        </p>
                      </td>
                    ))}
                  </tr>
                  <tr className="hover:bg-[var(--wl4-bg)]">
                    <td className="p-4 font-medium text-sm text-[var(--wl4-text)]">Ano</td>
                    {compareVehicles.map(vehicle => (
                      <td key={vehicle.id} className="p-4 text-center">
                        <p className="text-sm">{vehicle.modelYear}</p>
                      </td>
                    ))}
                  </tr>
                  <tr className="hover:bg-[var(--wl4-bg)]">
                    <td className="p-4 font-medium text-sm text-[var(--wl4-text)]">Quilometragem</td>
                    {compareVehicles.map(vehicle => (
                      <td key={vehicle.id} className="p-4 text-center">
                        <p className="text-sm">
                          {new Intl.NumberFormat('pt-BR').format(vehicle.mileage)} km
                        </p>
                      </td>
                    ))}
                  </tr>
                  <tr className="hover:bg-[var(--wl4-bg)]">
                    <td className="p-4 font-medium text-sm text-[var(--wl4-text)]">Combustível</td>
                    {compareVehicles.map(vehicle => (
                      <td key={vehicle.id} className="p-4 text-center">
                        <Badge variant="secondary">{vehicle.fuel}</Badge>
                      </td>
                    ))}
                  </tr>
                  <tr className="hover:bg-[var(--wl4-bg)]">
                    <td className="p-4 font-medium text-sm text-[var(--wl4-text)]">Câmbio</td>
                    {compareVehicles.map(vehicle => (
                      <td key={vehicle.id} className="p-4 text-center">
                        <Badge variant="secondary">{vehicle.transmission}</Badge>
                      </td>
                    ))}
                  </tr>
                  <tr className="hover:bg-[var(--wl4-bg)]">
                    <td className="p-4 font-medium text-sm text-[var(--wl4-text)]">Cor</td>
                    {compareVehicles.map(vehicle => (
                      <td key={vehicle.id} className="p-4 text-center">
                        <p className="text-sm">{vehicle.color}</p>
                      </td>
                    ))}
                  </tr>
                  <tr className="hover:bg-[var(--wl4-bg)]">
                    <td className="p-4 font-medium text-sm text-[var(--wl4-text)]">Portas</td>
                    {compareVehicles.map(vehicle => (
                      <td key={vehicle.id} className="p-4 text-center">
                        <p className="text-sm">{vehicle.doors}</p>
                      </td>
                    ))}
                  </tr>
                  {compareVehicles.some(v => v.isZeroKm !== undefined) && (
                    <tr className="hover:bg-[var(--wl4-bg)]">
                      <td className="p-4 font-medium text-sm text-[var(--wl4-text)]">Condição</td>
                      {compareVehicles.map(vehicle => (
                        <td key={vehicle.id} className="p-4 text-center">
                          <Badge variant={vehicle.isZeroKm ? 'default' : 'secondary'}>
                            {vehicle.isZeroKm || vehicle.mileage === 0 ? 'Zero KM' : 'Seminovo'}
                          </Badge>
                        </td>
                      ))}
                    </tr>
                  )}
                  <tr className="hover:bg-[var(--wl4-bg)]">
                    <td className="p-4 font-medium text-sm" />
                    {compareVehicles.map(vehicle => (
                      <td key={vehicle.id} className="p-4 text-center">
                        <Button
                          size="sm"
                          onClick={async () => {
                            const slug = await generateVehicleSlug(vehicle);
                            navigate(`/estoque/${slug}`);
                          }}
                          className="rounded-lg text-white font-semibold"
                          style={{ backgroundColor: primaryColor }}
                        >
                          Ver detalhes
                        </Button>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Favorites;
